import { useAtom, useSetAtom } from 'jotai';
import { type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { alignmentWalkAtom } from '@/atoms/alignment.atom';
import { stationVolumeHoverBinAtom, stationVolumeSelectionAtom } from '@/atoms/stationVolumes.atom';
import type { ClearanceBin, ClearanceJson } from '@/lib/clearance/types';
import { binCentreStation } from '@/lib/stationVolumes/aggregate';
import { formatFixed, formatStationRange } from '@/lib/stationVolumes/format';

export interface ClearanceTableProps {
  json: ClearanceJson;
  layerId: string;
}

/** An empty cell is an em dash, never a 0: "no violation in this bin" and
 *  "a violation of exactly zero depth" are different facts. */
const cell = (v: number | null, decimals: number): string => (v === null ? '—' : formatFixed(v, decimals));

/** The bands the RUN used, in run order — never the three literals the header
 *  strings used to hardcode. K2 fixes them at 0.1 / 0.2 / 0.35 m in v1 and says
 *  configurability comes later; a hardcoded `≤ 0.35 m` would start lying the
 *  day it does, silently, in five languages at once (M9). */
const NEAR_MISS_READERS: ((b: ClearanceBin) => number)[] = [
  (b) => b.nearMiss.within0_1M,
  (b) => b.nearMiss.within0_2M,
  (b) => b.nearMiss.within0_35M,
];
const NEAR_MISS_TESTIDS = ['near01', 'near02', 'near035'] as const;
/** Fallback for a run whose params predate `nearMissBandsM`. */
const DEFAULT_BANDS = [0.1, 0.2, 0.35];

export const ClearanceTable = ({ json, layerId }: ClearanceTableProps) => {
  const { t } = useTranslation('project');
  const [selection, setSelection] = useAtom(stationVolumeSelectionAtom);
  const setHover = useSetAtom(stationVolumeHoverBinAtom);
  const setWalk = useSetAtom(alignmentWalkAtom);
  const sel = selection && selection.layerId === layerId ? selection : null;
  const bands = json.params.nearMissBandsM?.length ? json.params.nearMissBandsM : DEFAULT_BANDS;

  /** Selecting a row moves the walk to it, on exactly the terms the chart's
   *  bars do: the cursor to the bin's centre, `follow` untouched, and nothing
   *  at all unless a walk is already running on this run's alignment. */
  const select = (b: ClearanceBin) => {
    setSelection({ layerId, fromBin: b.i, toBin: b.i });
    const station = binCentreStation(b);
    setWalk((w) => (w && w.key === json.alignment.key ? { ...w, station } : w));
  };
  const onRowKeyDown = (e: ReactKeyboardEvent<HTMLTableRowElement>, b: ClearanceBin) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      select(b);
      return;
    }
    if (e.key === 'Escape' && sel) {
      e.preventDefault();
      setSelection(null);
    }
  };

  return (
    <div className='min-w-0 overflow-auto'>
      {/* Finding I5. The offset column is the single most confusable quantity
          in the feature — a `−2.00` is a violation two metres to the LEFT, and
          nothing on screen said so. Stated once, here, above the column that
          carries it; the profile editor's own offset header points at the same
          sentence as a tooltip rather than repeating it. */}
      <p data-testid='clearance-offset-convention' className='mb-1 text-[11px] text-slate-500 dark:text-stone-400'>
        {t('clearance.offsetConvention')}
      </p>
      <table className='w-full text-xs tabular-nums'>
        <caption className='sr-only'>{t('clearance.table.caption')}</caption>
        <thead className='sticky top-0 bg-slate-50 dark:bg-stone-800'>
          <tr>
            <th scope='col' className='px-2 py-1 text-left'>
              {t('clearance.table.station')}
            </th>
            <th scope='col' className='px-2 py-1 text-right'>
              {t('clearance.table.points')}
            </th>
            <th scope='col' className='px-2 py-1 text-right'>
              {t('clearance.table.violations')}
            </th>
            <th scope='col' className='px-2 py-1 text-right'>
              {t('clearance.table.maxIntrusion')}
            </th>
            <th scope='col' className='px-2 py-1 text-right' title={t('clearance.offsetConvention')}>
              {t('clearance.table.worstOffset')}
            </th>
            <th scope='col' className='px-2 py-1 text-right'>
              {t('clearance.table.worstHeight')}
            </th>
            {/* Ruling K2: the near-miss ladder. Nested counts, so the unit is
                stated once in the header and the three columns read as one
                widening band rather than three independent numbers. */}
            {NEAR_MISS_TESTIDS.map((id, k) => (
              <th key={id} scope='col' className='px-2 py-1 text-right' title={t('clearance.table.nearMissHelp')}>
                {t('clearance.table.nearMissBand', { value: formatFixed(bands[k] ?? DEFAULT_BANDS[k], 2) })}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {json.bins.map((b) => (
            <tr
              key={b.i}
              data-testid={`clearance-row-${b.i}`}
              // Brushing is the thing the KPIs, the Profile highlight and any
              // future 3D sync hang off, so it cannot be mouse-only (M7).
              tabIndex={0}
              aria-current={sel && b.i >= sel.fromBin && b.i <= sel.toBin ? 'true' : undefined}
              className='cursor-pointer hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none dark:hover:bg-stone-800'
              onPointerEnter={() => setHover(b.i)}
              onPointerLeave={() => setHover(null)}
              onFocus={() => setHover(b.i)}
              onBlur={() => setHover(null)}
              onKeyDown={(e) => onRowKeyDown(e, b)}
              onClick={() => select(b)}>
              <th scope='row' className='px-2 py-0.5 text-left font-normal'>
                {formatStationRange(b, 2)}
              </th>
              <td className='px-2 py-0.5 text-right'>{b.pointCount.toLocaleString()}</td>
              <td className='px-2 py-0.5 text-right'>{b.violationCount.toLocaleString()}</td>
              <td data-testid={`clearance-cell-max-${b.i}`} className='px-2 py-0.5 text-right'>
                {cell(b.maxIntrusionM, 3)}
              </td>
              <td className='px-2 py-0.5 text-right'>{cell(b.worstOffsetM, 2)}</td>
              <td className='px-2 py-0.5 text-right'>{cell(b.worstHeightM, 2)}</td>
              {NEAR_MISS_READERS.map((read, k) => (
                <td
                  key={NEAR_MISS_TESTIDS[k]}
                  data-testid={`clearance-cell-${NEAR_MISS_TESTIDS[k]}-${b.i}`}
                  className='px-2 py-0.5 text-right'>
                  {read(b)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
